import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, BehaviorSubject, of, throwError, combineLatest } from 'rxjs';
import { tap, pluck, catchError, switchMapTo, map, shareReplay, concatMapTo, delay, concatMap, switchMap, filter, startWith } from 'rxjs/operators';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import {
	AuthResponse,
	User,
	UserLoginData,
	UserCreate,
	UserBankAccount,
	UserBankAccountUpdate,
	UserPasswordResetData,
	UserConfirmationData,
	UserPasswordResetRequestData,
	UserProfileDataUpdate,
	UserAddress,
	UserAddressData,
	UserAddressUpdate,
	SuggestionCity,
} from '../models';
import { environment } from '@app-env/environment';
import { ApiErrorResponse, ApiResponse, ApiSuccessResponse } from '@app/core/models';
import * as Sentry from '@sentry/angular';
import { catchApiErrorAndRethrow, filterNotNullish } from '@app/core/utils/rxjs';
import { Parcel, ParcelFromAPI } from '@app/ecsomag/models';
import { Order } from '@app/ecsomag/models/order';
import { Store, select } from '@ngrx/store';
import * as ShippingPricesStore from '@app/shared/store/prices';
import * as AuthStore from '../store/auth';
import { mapApiToEntity, ParcelEntity } from '@app/ecsomag/parcel/store/order/model';

@Injectable({
	providedIn: 'root',
})
export class UserService {
	public readonly user$ = new BehaviorSubject<User | null | undefined>(undefined);

	public readonly extraServices$ = this.shippingPricesStore.pipe(select(ShippingPricesStore.selectors.selectShippingExtraServices), filterNotNullish(), shareReplay(1));

	constructor(
		private _http: HttpClient,
		private _router: Router,
		private _jwt: JwtHelperService,
		private authStore: Store<AuthStore.State>,
		private shippingPricesStore: Store<ShippingPricesStore.State>
	) {
		setTimeout(() => {
			this.silentLogin().subscribe();
		}, 1000);
	}

	private _refreshTokenTimer?: { timeoutID: number; delay: number; delaySec: string; startedAt: Date };

	private _setUser(user: User | null) {
		this.user$.next(user);
		this.authStore.dispatch(AuthStore.actions.set({ user }));
	}

	private _getToken(): string | null {
		return localStorage.getItem('access_token');
	}

	private _setToken(token: string) {
		localStorage.setItem('access_token', token);
	}

	private _removeToken() {
		localStorage.removeItem('access_token');
	}

	private hasValidAccessToken = (): boolean => {
		// console.group('hasValidAccessToken');

		const token = this._getToken();

		if (!token) {
			// console.log('access token not found.');
			// console.log('return', false);
			// console.groupEnd();
			return false;
		}

		let isTokenInvalid = false;
		let isTokenExpired = false;

		try {
			isTokenExpired = this._jwt.isTokenExpired(token);
		} catch (e) {
			isTokenInvalid = true;
		}

		if (this._refreshTokenTimer) {
			// console.log('clearTimeout', this._refreshTokenTimer.timeoutID);
			clearTimeout(this._refreshTokenTimer.timeoutID);
			this._refreshTokenTimer = undefined;
		}

		if (isTokenInvalid || isTokenExpired) {
			// console.log('access token invalid or expired:', { isTokenInvalid, isTokenExpired });
			// console.log('unset user');
			// console.log('return', false);
			// console.groupEnd();
			this._setUser(null);
			this._removeToken();
			return false;
		} else {
			const exp = this._jwt.getTokenExpirationDate();
			// console.log('access token valid, expiration:', exp);
			if (exp instanceof Date) {
				const startedAt = new Date();
				const delay = exp.getTime() - startedAt.getTime() - 30_000;
				const timeoutID = window.setTimeout(() => {
					// console.log('calling refresh token from timer...');
					this.refreshToken().subscribe();
				}, delay);
				this._refreshTokenTimer = {
					timeoutID,
					delay,
					delaySec: `${Math.floor(delay / 1000 / 60)}:${Math.floor((delay / 1000) % 60)}.${Math.floor(delay % 1000)}`,
					startedAt,
				};
				// console.log('setTimeout', this._refreshTokenTimer);
			}
			// console.log('return', true);
			// console.groupEnd();
			return true;
		}
	};

	private refreshToken(): Observable<boolean> {
		return this._http.post<ApiResponse<AuthResponse>>(environment.API_URL + 'user/authorize', {}).pipe(
			pluck('data'),
			tap(auth => {
				if (auth) {
					// console.log('refreshed access token', auth);
					this._setToken(auth.accessToken);
				} else {
					// console.log('refreshing access token failed', auth);
				}
			}),
			map(auth => !!auth),
			catchError(() => of(false)),
			shareReplay(1)
		);
	}

	public hasLoggedInUser(): Observable<boolean> {
		return (this.hasValidAccessToken() ? of(true) : this.refreshToken()).pipe(
			tap(hasLoggedInUser => {
				// console.log('hasLoggedInUser()', hasLoggedInUser);
			}),
			shareReplay(1)
		);
	}

	public getLoggedInUser(): Observable<User | null> {
		return this.hasLoggedInUser().pipe(
			switchMap(hasLoggedInUser => {
				if (hasLoggedInUser) {
					const user = this.user$.getValue();
					if (user) {
						// console.log('user$ = ', user, ' -> user$');
						return of(user);
					} else {
						// console.log('user$ = ', user, ' -> loadLoggedInUser()');
						return this.loadLoggedInUser();
					}
				} else {
					// console.log('user$ = <any> -> null');
					return of(null);
				}
			}),
			shareReplay(1)
		);
	}

	public silentLogin(): Observable<User | null> {
		// console.log('silentLogin()');
		return this.getLoggedInUser().pipe(
			tap(user => {
				this._setUser(user);
			})
		);
		// return this.hasLoggedInUser().pipe(
		// 	tap(hasLoggedInUser => {
		// 		console.log('silentLogin(): switchmap to getLoggedInUser()', hasLoggedInUser);
		// 	}),
		// 	switchMap(hasLoggedInUser => this.loadLoggedInUser()),
		// 	shareReplay(1)
		// );
	}

	private loadLoggedInUser(): Observable<User | null> {
		if (this.hasValidAccessToken()) {
			return this._http.get<ApiResponse<User>>(environment.API_URL + 'user').pipe(
				pluck('data'),
				catchApiErrorAndRethrow(),
				catchError(error => {
					// console.log('user error', error);
					// this._setUser(null);
					// this._removeToken();
					return of(null);
				}),
				shareReplay(1)
			);
		} else {
			return of(null);
		}
	}

	// login / logout

	public login(data: UserLoginData): Observable<User | null> {
		return this._http.post<ApiResponse<AuthResponse>>(environment.API_URL + 'user/login', data).pipe(
			pluck('data'),
			tap(auth => {
				// console.log('user logged in', auth);
				if (auth) {
					this._setToken(auth.accessToken);
				}
			}),
			switchMap(auth => this.getLoggedInUser()),
			tap(user => {
				this._setUser(user);
			}),
			catchApiErrorAndRethrow(),
			shareReplay(1)
		);
	}

	public logout() {
		return this._http.post<ApiSuccessResponse>(environment.API_URL + 'user/logout', {}).pipe(
			catchError(error => {
				// console.log('logout error tap', error);
				return of(null);
			}),
			tap(_ => {
				// console.log('user logged out');
				this._setUser(null);
				this._removeToken();
			}),
			shareReplay(1)
		);
	}

	// registration

	public register(data: UserCreate): Observable<boolean> {
		const url = environment.API_URL + 'user/create';

		return this._http.post<ApiSuccessResponse>(url, data).pipe(
			pluck('data', 'success'),
			tap(response => {
				// console.log('register', response);
			}),
			catchApiErrorAndRethrow(),
			shareReplay(1)
		);
	}

	public resendConfirmation(data: UserConfirmationData): Observable<boolean> {
		const url = environment.API_URL + 'user/confirmation/resend';

		return this._http.post<ApiSuccessResponse>(url, data).pipe(
			pluck('data', 'success'),
			tap(response => {
				// console.log('resendConfirmation', response);
			}),
			catchApiErrorAndRethrow(),
			shareReplay(1)
		);
	}

	// password reset

	public requestPasswordReset(data: UserPasswordResetRequestData): Observable<boolean> {
		const url = environment.API_URL + 'user/password/reset';

		return this._http.post<ApiSuccessResponse>(url, data).pipe(
			pluck('data', 'success'),
			tap(response => {
				// console.log('requestPasswordReset', response);
			}),
			catchError((error: HttpErrorResponse) => {
				Sentry.withScope(scope => {
					scope.setTag('module', 'site.userService');
					scope.setExtra('error', error);
					Sentry.captureMessage('API error');
				});
				return throwError((error.error as ApiErrorResponse).errors);
			}),
			shareReplay(1)
		);
	}

	public resetPassword(data: UserPasswordResetData): Observable<boolean> {
		const url = environment.API_URL + 'user/password/confirmation';

		return this._http.post<ApiSuccessResponse>(url, data).pipe(
			pluck('data', 'success'),
			tap(response => {
				// console.log('resetPassword', response);
			}),
			catchApiErrorAndRethrow(),
			shareReplay(1)
		);
	}

	// profile data

	public updateProfileData(data: UserProfileDataUpdate): Observable<User | null> {
		const url = environment.API_URL + 'user';

		return this._http.put<ApiSuccessResponse>(url, data).pipe(
			pluck('data', 'success'),
			tap(response => {
				// console.log('updateProfileData', response);
			}),
			catchApiErrorAndRethrow(),
			concatMap(() => this.loadLoggedInUser()),
			shareReplay(1)
		);
	}

	// bank account

	public getBankAccount(): Observable<UserBankAccount | null> {
		if (!this.hasValidAccessToken()) return of(null);

		const url = environment.API_URL + 'user/bank-account';

		return this._http.get<ApiResponse<UserBankAccount>>(url).pipe(
			pluck('data'),
			map(bankAccount => (bankAccount.bankAccountNumber ? bankAccount : null)),
			tap(response => {
				// console.log('getUserBankAccount', response);
			}),
			// catchApiErrorAndRethrow(),
			catchError((error: HttpErrorResponse) => {
				// Sentry.withScope(scope => {
				// 	scope.setTag('module', 'site.userService');
				// 	scope.setExtra('error', error);
				// 	Sentry.captureMessage('API error');
				// });
				// return throwError((error.error as ApiErrorResponse).errors);
				return of(null);
			}),
			shareReplay(1)
		);
	}

	public updateBankAccount(data: UserBankAccountUpdate): Observable<boolean> {
		const url = environment.API_URL + 'user/bank-account';

		return this._http.put<ApiSuccessResponse>(url, data).pipe(
			pluck('data', 'success'),
			tap(response => {
				// console.log('updateBankAccount', response);
			}),
			catchApiErrorAndRethrow(),
			shareReplay(1)
		);
	}

	// addresses

	public getAddresses(): Observable<UserAddress[]> {
		if (!this.hasValidAccessToken()) return of([]);

		const url = environment.API_URL + 'user/addresses';

		return this._http.get<ApiResponse<UserAddress[]>>(url).pipe(
			pluck('data'),
			tap(response => {
				// console.log('getAddresses', response);
			}),
			catchApiErrorAndRethrow(),
			catchError(() => of([])),
			shareReplay(1)
		);
	}

	public createAddress(data: UserAddressData): Observable<{ id: UserAddress['id'] }> {
		const url = environment.API_URL + 'user/addresses';

		return this._http.post<ApiResponse<{ id: UserAddress['id'] }>>(url, data).pipe(
			pluck('data'),
			tap(response => {
				// console.log('createAddress', response);
			}),
			catchApiErrorAndRethrow(),
			shareReplay(1)
		);
	}

	public updateAddress(id: UserAddress['id'], update: UserAddressUpdate): Observable<boolean> {
		const url = environment.API_URL + `user/addresses/${id}`;

		return this._http.put<ApiSuccessResponse>(url, update).pipe(
			pluck('data', 'success'),
			tap(response => {
				// console.log('updateAddress', response);
			}),
			catchApiErrorAndRethrow(),
			shareReplay(1)
		);
	}

	public deleteAddress(id: UserAddress['id']): Observable<boolean> {
		const url = environment.API_URL + `user/addresses/${id}`;

		return this._http.delete<ApiSuccessResponse>(url).pipe(
			pluck('data', 'success'),
			tap(response => {
				// console.log('deleteAddress', response);
			}),
			catchApiErrorAndRethrow(),
			shareReplay(1)
		);
	}

	public getCitiesByZipCode(isoCode: string, zipCode: string): Observable<SuggestionCity[]> {
		const url = environment.API_URL + 'calculator/zip-codes';
		const params = { isoCode, zipCode };

		return this._http
			.get<ApiResponse<SuggestionCity[]>>(url, {
				params,
			})
			.pipe(
				pluck('data'),
				tap(response => {
					// console.log('getCities', response);
				}),
				catchApiErrorAndRethrow(),
				catchError(() => of([])),
				shareReplay(1)
			);
	}

	public getCitiesByQuery(isoCode: string, query: string): Observable<SuggestionCity[]> {
		const url = environment.API_URL + 'calculator/zip-codes';
		const params = { isoCode, query };

		return this._http
			.get<ApiResponse<SuggestionCity[]>>(url, {
				params,
			})
			.pipe(
				pluck('data'),
				tap(response => {
					// console.log('getCities', response);
				}),
				catchApiErrorAndRethrow(),
				catchError(() => of([])),
				shareReplay(1)
			);
	}

	// orders and parcels

	public getPendingOrders(): Observable<Order[] | null> {
		if (!this.hasValidAccessToken()) return of(null);

		const url = environment.API_URL + 'user/orders';

		return this._http.get<ApiResponse<Order[]>>(url).pipe(
			pluck('data'),
			map(orders => orders.filter(order => !order.finished).sort((a, b) => -a.updatedAt.localeCompare(b.updatedAt))),
			tap(response => {
				// console.log('getPendingOrders', response);
			}),
			catchApiErrorAndRethrow(),
			shareReplay(1)
		);
	}

	public deleteOrder(id: Order['id']): Observable<boolean> {
		if (!this.hasValidAccessToken()) return of(false);

		const url = environment.API_URL + `orders/${id}`;

		return this._http.delete<ApiSuccessResponse>(url).pipe(
			pluck('data', 'success'),
			tap(response => {
				// console.log('deleteOrder', response);
			}),
			catchApiErrorAndRethrow(),
			shareReplay(1)
		);
	}

	public getDispatchedParcels() {
		if (!this.hasValidAccessToken()) return of(null);

		const url = environment.API_URL + 'user/parcels/history';

		return combineLatest([
			this.extraServices$,
			this._http.get<ApiResponse<ParcelFromAPI[]>>(url).pipe(
				pluck('data'),
				map(parcels => parcels.filter(parcel => parcel.parcelNumber)),
				tap(response => {
					// console.log('getDispatchedParcels', response);
				}),
				catchApiErrorAndRethrow(),
				shareReplay(1)
			),
		]).pipe(
			map(([extraServices, parcels]) => {
				return parcels.map(parcel => mapApiToEntity(extraServices, parcel));
			})
		);
	}

	// delete account

	public requestProfileDeletion(): Observable<boolean> {
		const url = environment.API_URL + 'user';

		return this._http.delete<ApiSuccessResponse>(url).pipe(
			pluck('data', 'success'),
			tap(response => {
				// console.log('requestProfileDeletion', response);
			}),
			catchError((error: HttpErrorResponse) => {
				Sentry.withScope(scope => {
					scope.setTag('module', 'site.userService');
					scope.setExtra('error', error);
					Sentry.captureMessage('API error');
				});
				return throwError((error.error as ApiErrorResponse).errors);
			}),
			shareReplay(1)
		);
	}
}
